// glonet-react-tailwind-project--src--components--GlonetAiLandingV2.js
// The simplified glonet.ai landing (owner direction, 2026-08-11): minimal
// time, minimal cognitive load. One headline, one sentence of how-it-works,
// two doors — talk to the matchmaker, or step into the Water Cooler. The
// how-it-works grid, matchmaker section, join form and floating door are
// gone; the proof-of-value email card and the Water Cooler section remain.
//
// "Talk first, sign in after": Start talking runs the REAL intake agent with
// no account (VoiceOnboarding anonymous mode). The agent's saves land in a
// localStorage draft; when the session ends we ask "where should we send
// your matches?" and the draft is written through the normal authenticated
// register/update path right after sign-in (Google redirect included —
// the draft survives because it lives in localStorage, not React state).
//
// Selected by wc_settings.landing_variant (GTM tab → Settings → Landing
// page) via GlonetAiLanding.js; the frozen original is GlonetAiLandingClassic.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Auth } from "aws-amplify";
import api from "../services/api";
import { API_PATHS } from "../config/apiConfig";
import { GLONET_AI_EVENT_ID } from "../config/glonetai";
import VoiceOnboarding from "./VoiceOnboarding";
import { startGoogleSignIn } from "./GoogleSignInButton";
import { readVoiceDraft, clearVoiceDraft } from "../utils/voiceDraft";
import log from "../utils/logger";

const ACCENT = "#74a0f5";
const ACCENT_STRONG = "#5b8cf0";
const GLOW = "rgba(116,160,245,0.22)";
const LIVE_DOT = "#57d9a4"; // the cooler is always open

const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

// Anonymous sessions cost real ElevenLabs credits — a browser gets two free
// runs a day before "Start talking" routes through sign-in instead.
const ANON_RUNS_KEY = "ga_anon_voice_runs";
const ANON_RUNS_PER_DAY = 2;
function anonRunsToday() {
  try {
    const raw = JSON.parse(localStorage.getItem(ANON_RUNS_KEY) || "null");
    const today = new Date().toDateString();
    return raw && raw.day === today ? raw.count || 0 : 0;
  } catch (e) { return 0; }
}
function bumpAnonRuns() {
  try {
    localStorage.setItem(ANON_RUNS_KEY, JSON.stringify({
      day: new Date().toDateString(), count: anonRunsToday() + 1,
    }));
  } catch (e) { /* storage off — fail open, the session cap still holds */ }
}

function WaveBars({ playing }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "3px", height: "30px", flex: 1 }}>
      {Array.from({ length: 40 }).map((_, i) => {
        const h = 5 + Math.abs(Math.sin(i * 0.6 + 1)) * 22;
        return (
          <span
            key={i}
            style={{
              flex: 1,
              minWidth: "2px",
              height: h + "px",
              borderRadius: "2px",
              background: playing ? ACCENT : "rgba(255,255,255,0.15)",
              transformOrigin: "center",
              animation: playing ? `ga-wave 0.85s ease-in-out ${(i % 7) * 0.07}s infinite` : "none",
            }}
          />
        );
      })}
    </div>
  );
}

const MicIcon = ({ size = 17, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.7" strokeLinecap="round">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11 a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="21" />
  </svg>
);

const DoorIcon = ({ size = 16, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.7">
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <circle cx="14.5" cy="12" r="1" fill={stroke} stroke="none" />
  </svg>
);

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function GlonetAiLandingV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const [voiceOn, setVoiceOn] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [matchingInfo, setMatchingInfo] = useState(null); // {has_profile, is_attendee, invitation_code,...}
  const [authEmail, setAuthEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [convOn, setConvOn] = useState(false);    // agent-conversation pipeline switch (pitch copy honesty)
  const [micSecs, setMicSecs] = useState(5);      // silent-mic prompt threshold (admin-tunable)
  const [appLinkOn, setAppLinkOn] = useState(false); // footer "The app" link — admin opt-in, hidden by default
  const [askSignin, setAskSignin] = useState(false); // "where should we send your matches?" after an anon session
  const [draftNote, setDraftNote] = useState("");    // status of the post-sign-in draft save
  const draftBusyRef = useRef(false);

  // Where this landing lives right now: "/" on the glonet.ai host, "/glonet-ai"
  // elsewhere. Used as the return target for sign-in/watercooler round-trips.
  const landingPath = location.pathname === "/" ? "/" : "/glonet-ai";
  const voiceReturnPath = landingPath === "/" ? "/?voice=1" : "/glonet-ai?voice=1";
  // After sign-in with a parked draft, come back here — the mount effect
  // below finds the draft and writes it through the real path.
  const draftReturnPath = landingPath === "/" ? "/?draft=1" : "/glonet-ai?draft=1";

  // Auth check + "who's around" signal; ?voice=1 reopens the widget after sign-in return.
  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async (u) => {
        setIsAuthed(true);
        setAuthEmail((u && u.attributes && u.attributes.email) || "");
        api.get(API_PATHS.USERS.PROFILE)
          .then(({ data }) => setIsAdmin(!!data.is_admin))
          .catch(() => {});
        try {
          const { data } = await api.get(
            API_PATHS.EVENTS.MY_MATCHING_INFO.replace("{eventId}", GLONET_AI_EVENT_ID));
          setMatchingInfo(data);
          await maybeSaveDraft(data);
        } catch (err) {
          log.error("my-matching-info fetch failed", { error: err });
        }
        if (new URLSearchParams(location.search).get("voice") === "1") setVoiceOn(true);
      })
      .catch(() => {
        setIsAuthed(false);
        // returning visitor who talked but never signed in: re-offer the door
        if (readVoiceDraft()) setAskSignin(true);
      });
    api.get(API_PATHS.PUBLIC.WATERCOOLER_ACTIVITY)
      .then(({ data }) => {
        setActiveCount(data.active_count || 0);
        setConvOn(!!data.conversations_enabled);   // pitch copy tracks the real switch
        if (data.mic_silence_secs) setMicSecs(data.mic_silence_secs);
        setAppLinkOn(!!data.show_app_link);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // The parked anonymous draft -> the member's real profile, right after
  // sign-in. Same tier rules as talking while signed in: a priced tier goes
  // through Stripe first (draft survives the round-trip in localStorage).
  const maybeSaveDraft = async (info) => {
    const draft = readVoiceDraft();
    if (!draft || draftBusyRef.current) return;
    draftBusyRef.current = true;
    try {
      if (!info.is_attendee) {
        try {
          const { data } = await api.get(
            API_PATHS.EVENTS.TIER_STATUS.replace("{eventId}", GLONET_AI_EVENT_ID));
          const price = data.available_tier ? parseFloat(data.available_tier.price) : 0;
          if (price > 0) {
            const paidPath = data.available_tier?.pricing_mode === "subscription"
              ? "subscribe" : "pricing";
            navigate(`/events/${GLONET_AI_EVENT_ID}/${paidPath}`, {
              state: {
                invitationCode: info.invitation_code,
                eventId: GLONET_AI_EVENT_ID,
                returnTo: draftReturnPath,
              },
            });
            return;
          }
        } catch (err) {
          log.error("tier-status check failed; proceeding as free", { error: err });
        }
      }
      const merged = {
        objective: draft.objective || info.objective || "",
        offering: draft.offering || info.offering || "",
      };
      if (info.is_attendee) {
        await api.put(API_PATHS.EVENTS.UPDATE.replace("{eventId}", GLONET_AI_EVENT_ID), merged);
      } else {
        await api.post(
          API_PATHS.EVENTS.REGISTER
            .replace("{eventId}", GLONET_AI_EVENT_ID)
            .replace("{invitation_code}", info.invitation_code),
          merged);
      }
      clearVoiceDraft();
      setAskSignin(false);
      setMatchingInfo((prev) => ({ ...(prev || info), ...merged, is_attendee: true, has_profile: true }));
      setDraftNote("Got it — the matchmaker is on it. Watch your inbox.");
    } catch (err) {
      log.error("draft save after sign-in failed", { error: err });
      setDraftNote("Couldn't save what you told the agent — tap the mic to try again.");
    } finally {
      draftBusyRef.current = false;
    }
  };

  // Honest gate-based label: entries in the last ~10 min, not "online now".
  const liveLabel = activeCount > 0
    ? `${activeCount} HERE IN THE LAST 10 MIN`
    : "ALWAYS OPEN";

  const hasProfile = !!(matchingInfo && matchingInfo.has_profile);

  // Start talking: signed out -> run the agent anonymously (up to the daily
  // browser cap, then via sign-in); signed in -> same flow as before (priced
  // tiers go through Stripe before voice opens).
  const startVoice = async () => {
    if (!isAuthed) {
      if (anonRunsToday() >= ANON_RUNS_PER_DAY) {
        navigate("/signin", { state: { from: voiceReturnPath } });
        return;
      }
      if (!voiceOn) bumpAnonRuns();
      setAskSignin(false);
      setVoiceOn((v) => !v);
      // the panel mounts on this state change — scroll after it exists
      setTimeout(() => {
        const el = document.getElementById("voice-demo");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
      return;
    }
    if (matchingInfo && !matchingInfo.is_attendee) {
      try {
        const { data } = await api.get(
          API_PATHS.EVENTS.TIER_STATUS.replace("{eventId}", GLONET_AI_EVENT_ID));
        const price = data.available_tier ? parseFloat(data.available_tier.price) : 0;
        if (price > 0) {
          const paidPath = data.available_tier?.pricing_mode === "subscription"
            ? "subscribe" : "pricing";
          navigate(`/events/${GLONET_AI_EVENT_ID}/${paidPath}`, {
            state: {
              invitationCode: matchingInfo.invitation_code,
              eventId: GLONET_AI_EVENT_ID,
              returnTo: voiceReturnPath,
            },
          });
          return;
        }
      } catch (err) {
        log.error("tier-status check failed; proceeding as free", { error: err });
      }
    }
    setVoiceOn((v) => !v);
  };

  // An anonymous session just ended: if the agent parked anything, ask where
  // to send the matches; otherwise back to the idle mic.
  const onAnonVoiceEnd = () => {
    setVoiceOn(false);
    if (readVoiceDraft()) setAskSignin(true);
  };

  const enterWaterCooler = (e) => {
    e.preventDefault();
    navigate("/watercooler", { state: { from: landingPath } });
  };

  const goHosts = (e) => {
    e.preventDefault();
    navigate("/hosts");
  };

  const goStory = (e) => {
    e.preventDefault();
    navigate("/watercooler-story");
  };

  const signOutHere = async () => {
    try { await Auth.signOut(); } catch (err) { /* already signed out */ }
    setIsAuthed(false);
    setAuthEmail("");
    setMatchingInfo(null);
    setVoiceOn(false);
    setDraftNote("");
  };

  return (
    <div
      style={{
        background: "#0c0d10",
        color: "#f1f2f4",
        minHeight: "100vh",
        fontFamily: "'Hanken Grotesk',system-ui,sans-serif",
        WebkitFontSmoothing: "antialiased",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <style>{`
        @keyframes ga-wave { 0%,100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }
        @keyframes ga-pulse { 0% { box-shadow: 0 0 0 0 rgba(87,217,164,0.5); } 70% { box-shadow: 0 0 0 7px rgba(87,217,164,0); } 100% { box-shadow: 0 0 0 0 rgba(87,217,164,0); } }
        @keyframes ga-breathe { 0%,100% { opacity: 0.55; transform: translateX(-50%) scale(1); } 50% { opacity: 0.8; transform: translateX(-50%) scale(1.06); } }
        @keyframes ga-floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .ga-link { color:#b6b9c0; text-decoration:none; transition:color .15s; }
        .ga-link:hover { color:#fff; }
        .ga-btn { transition:background .15s, border-color .15s; }
        .ga-btn-primary:hover { background:${ACCENT_STRONG} !important; }
        .ga-btn-ghost:hover { border-color:${ACCENT} !important; }
        .ga-foot { color:#7c818b; text-decoration:none; transition:color .15s; }
        .ga-foot:hover { color:#f1f2f4; }
        .ga-wc-what:hover { color:#fff !important; text-decoration:underline; }
        @media (max-width: 840px) {
          .ga-grid-2 { grid-template-columns:1fr !important; gap:40px !important; }
          .ga-nav-links a:not(.ga-nav-cta) { display:none; }
          /* phones: the mic strip is the demo of the PRIMARY door, so it sits
             between Start talking and the Water Cooler button (owner,
             2026-08-11). display:contents lifts the CTA row's children into
             the hero's flex column so order can interleave the strip. */
          /* phones: let the hero size to its content — a forced viewport
             height only manufactured scroll on short screens */
          .ga-hero { display:flex; flex-direction:column; align-items:center; min-height:0 !important; }
          .ga-cta-row { display:contents !important; }
          .ga-cta-talk { order:1; margin-top:40px; }
          #voice-demo { order:2; margin:28px 0 0 !important; width:100%; }
          .ga-cta-wc { order:3; margin-top:28px; }
          .ga-draft-note { order:4; }
        }
      `}</style>

      {/* ambient hero glow */}
      <div
        style={{
          position: "absolute",
          top: "-220px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "1000px",
          height: "680px",
          background: `radial-gradient(circle at center, ${GLOW} 0%, transparent 68%)`,
          filter: "blur(14px)",
          pointerEvents: "none",
          zIndex: 0,
          animation: "ga-breathe 9s ease-in-out infinite",
        }}
      />

      {/* NAV — no join CTA: the two hero doors are the only ways in */}
      <nav
        style={{
          position: "relative",
          zIndex: 3,
          maxWidth: "1140px",
          margin: "0 auto",
          padding: "26px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: "12px" }}>
          <span style={{ fontFamily: SERIF, fontSize: "25px", letterSpacing: "-0.01em" }}>
            glonet<span style={{ color: ACCENT }}>.ai</span>
          </span>
          <span style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>
            Global&nbsp;Network
          </span>
        </div>
        {/* No nav links: nothing scrolls, and both former links had twins on
            this same screen (Water Cooler -> the ⓘ line's page; For hosts ->
            the footer's "Run your own nexus"). Only the signed-in chrome
            (ADMIN / who's signed in) lives here. */}
        <div className="ga-nav-links" style={{ display: "flex", alignItems: "center", gap: "30px" }}>
          {isAuthed && isAdmin && (
            <a
              href="/admin"
              onClick={(e) => { e.preventDefault(); navigate("/admin"); }}
              className="ga-nav-cta"
              style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.1em", fontWeight: 700,
                       color: "#fbbf24", background: "transparent", border: "1px solid rgba(251,191,36,0.55)",
                       textDecoration: "none", padding: "9px 16px", borderRadius: "999px", whiteSpace: "nowrap" }}
            >
              ADMIN
            </a>
          )}
          {isAuthed && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", whiteSpace: "nowrap" }}>
              <span style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.06em", color: "#7c818b" }}>
                {authEmail || "signed in"}
              </span>
              <button
                onClick={signOutHere}
                style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.08em", cursor: "pointer",
                         color: "#f1f2f4", background: "transparent", border: "1px solid rgba(255,255,255,0.22)",
                         padding: "6px 12px", borderRadius: "999px" }}
              >
                SIGN OUT
              </button>
            </span>
          )}
        </div>
      </nav>

      {/* HERO — the whole pitch. Vertically centered in what's left of the
          viewport under the nav (100vh - nav - footer) so the one-screen
          intent reads as deliberate instead of truncated. */}
      <section className="ga-hero" style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "40px 32px 56px", textAlign: "center", boxSizing: "border-box", minHeight: "calc(100vh - 96px - 92px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: ACCENT, textTransform: "uppercase", marginBottom: "30px" }}>
          One global nexus
        </div>
        <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(42px,6.4vw,82px)", lineHeight: 1.02, letterSpacing: "-0.015em", margin: "0 auto", maxWidth: "14ch", textWrap: "balance" }}>
          People you want to meet are one introduction away.
        </h1>
        <p style={{ fontSize: "19px", lineHeight: 1.6, color: "#b6b9c0", maxWidth: "60ch", margin: "30px auto 0" }}>
          Say what you're looking for — out loud — and the matchmaker finds your people{convOn ? ", lets your agents meet first," : ""} and emails you when it's worth your time.
        </p>
        <div className="ga-cta-row" style={{ display: "flex", gap: "28px", justifyContent: "center", alignItems: "flex-start", flexWrap: "wrap", marginTop: "40px" }}>
          {/* left door + its micro-line (GDPR notice-at-collection lives HERE,
              since the mic panel no longer exists until a session starts) */}
          <div className="ga-cta-talk" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
            <button
              onClick={startVoice}
              className="ga-btn ga-btn-primary"
              style={{ display: "inline-flex", alignItems: "center", gap: "10px", fontFamily: "inherit", fontSize: "15px", fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none", padding: "15px 24px", borderRadius: "999px", cursor: "pointer" }}
            >
              <MicIcon />
              {hasProfile ? "Update matching info" : "Start talking"}
            </button>
            {!isAuthed && (
              <span style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.08em", lineHeight: 1.6, color: "#7c818b", maxWidth: "46ch", textAlign: "center" }}>
                voice builds your profile, discarded if you don't sign in ·{" "}
                <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
                   style={{ color: "#7c818b", textDecoration: "underline" }}>
                  privacy policy
                </a>
              </span>
            )}
          </div>
          <div className="ga-cta-wc" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
            <a
              href="/watercooler"
              onClick={enterWaterCooler}
              className="ga-btn ga-btn-ghost"
              style={{ display: "inline-flex", alignItems: "center", gap: "10px", fontSize: "15px", fontWeight: 500, color: "#f1f2f4", background: "transparent", border: "1px solid rgba(255,255,255,0.18)", padding: "15px 22px", borderRadius: "999px", textDecoration: "none" }}
            >
              <DoorIcon />
              Enter the Water Cooler
            </a>
            {/* status + what-it-is on one line; the ⓘ link opens the explainer page */}
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontFamily: MONO, fontSize: "11px", letterSpacing: "0.14em" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", color: LIVE_DOT }}>
                <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: LIVE_DOT, animation: "ga-pulse 1.8s infinite" }} />
                {liveLabel}
              </span>
              <span style={{ color: "#5c616b" }}>·</span>
              {/* the ONLY thing on the page that says what the Water Cooler
                  IS, so it reads as text, not as fine print: body sans, the
                  subhead's color — brighter than the status beside it. */}
              <a href="/watercooler-story" onClick={goStory} className="ga-link ga-wc-what"
                 style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontFamily: "'Hanken Grotesk',system-ui,sans-serif", fontSize: "13px", letterSpacing: "0", color: "#b6b9c0" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="9" />
                  <line x1="12" y1="10.5" x2="12" y2="16.5" strokeLinecap="round" />
                  <circle cx="12" cy="7.6" r="0.6" fill="currentColor" stroke="none" />
                </svg>
                a virtual meeting space
              </a>
            </span>
          </div>
        </div>

        {/* the voice panel exists only while something is happening — a live
            session, or the where-should-we-send-it prompt after one. Idle
            state has no panel: the button is the one entry (owner, no
            redundancy). */}
        {(voiceOn || (askSignin && !isAuthed)) && (
        <div id="voice-demo" style={{ maxWidth: "560px", margin: "48px auto 0", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "18px", padding: "22px 24px", textAlign: "left" }}>
          {voiceOn ? (
            <VoiceOnboarding
              micSilenceSecs={micSecs}
              anonymous={!isAuthed}
              onEnd={() => {
                if (!isAuthed) { onAnonVoiceEnd(); return; }
                setVoiceOn(false);
                // refresh so the button flips to "Update matching info" after first save
                api.get(API_PATHS.EVENTS.MY_MATCHING_INFO.replace("{eventId}", GLONET_AI_EVENT_ID))
                  .then(({ data }) => setMatchingInfo(data))
                  .catch(() => {});
              }}
            />
          ) : (
            /* the anonymous session parked a draft — one step left */
            <div>
              <div style={{ fontSize: "15px", color: "#f1f2f4", fontWeight: 600 }}>
                Where should we send your matches?
              </div>
              <div style={{ fontSize: "13px", color: "#9a9ea7", marginTop: "4px" }}>
                Sign in and the matchmaker gets to work. Your answers are saved on this device for an hour.
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
                <button
                  onClick={() => startGoogleSignIn({ from: draftReturnPath })}
                  className="ga-btn"
                  style={{ display: "inline-flex", alignItems: "center", gap: "9px", fontFamily: "inherit", fontSize: "14px", fontWeight: 600, color: "#1f2126", background: "#fff", border: "none", padding: "11px 18px", borderRadius: "999px", cursor: "pointer" }}
                >
                  <GoogleG />
                  Continue with Google
                </button>
                {/* anonymous talkers are mostly NEW people — signup first;
                    the signup page links back to sign-in for existing accounts */}
                <button
                  onClick={() => navigate("/signup", { state: { from: draftReturnPath } })}
                  className="ga-btn ga-btn-ghost"
                  style={{ display: "inline-flex", alignItems: "center", gap: "9px", fontFamily: "inherit", fontSize: "14px", fontWeight: 500, color: "#f1f2f4", background: "transparent", border: "1px solid rgba(255,255,255,0.18)", padding: "11px 18px", borderRadius: "999px", cursor: "pointer" }}
                >
                  Create account
                </button>
                <button
                  onClick={startVoice}
                  style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.08em", color: "#7c818b", background: "none", border: "none", cursor: "pointer" }}
                >
                  TALK AGAIN
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* post-sign-in draft status ("Got it — the matchmaker is on it.") */}
        {draftNote && !voiceOn && (
          <div className="ga-draft-note" style={{ marginTop: "28px", fontSize: "14px", color: LIVE_DOT }}>{draftNote}</div>
        )}
      </section>

      {/* The email-proof card and the Water Cooler section are gone (owner,
          2026-08-11): the landing is ONE screen — the voice demo is the proof,
          and the Water Cooler explainer lives at /watercooler-story, linked
          from the ⓘ line under the hero button. */}

      {/* AGENTS — whole section only exists while the conversation pipeline is
          on; premium framing: transcripts free, audio paid */}
      {convOn && (
      <>
      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />
      <section id="agents" className="ga-grid-2" style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px", display: "grid", gridTemplateColumns: "1fr 1.15fr", gap: "64px", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>
            Agent pre-screen
            <span style={{ marginLeft: "10px", fontSize: "10px", letterSpacing: "0.18em", color: "#0c0d10", background: ACCENT, padding: "3px 8px", borderRadius: "999px" }}>PREMIUM</span>
          </div>
          <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,48px)", lineHeight: 1.06, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "14ch" }}>
            Your agents meet before you do.
          </h2>
          <p style={{ fontSize: "18px", lineHeight: 1.65, color: "#b6b9c0", margin: "22px 0 0", maxWidth: "44ch" }}>
            Each agent represents your objective — and respects your dealbreakers. They hold a short, honest pre-meeting so you can listen in and decide if it's worth a real conversation.
          </p>
          <p style={{ fontSize: "14px", lineHeight: 1.6, color: "#7c818b", margin: "14px 0 0", maxWidth: "44ch" }}>
            Every member reads the transcript. Premium members get the audio — with their best match, every week.
          </p>
        </div>
        {/* transcript card */}
        <div style={{ background: "#131519", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "20px", padding: "24px", boxShadow: "0 24px 60px -30px rgba(0,0,0,0.7)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <span style={{ fontSize: "14px", fontWeight: 600 }}>Dana × Marcus — pre-meet</span>
            <span style={{ fontFamily: MONO, fontSize: "11px", color: "#7c818b" }}>94% fit</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px", padding: "18px 0" }}>
            {[
              ["DANA·AI", ACCENT, "She's raising a seed round for agent infrastructure and wants a lead who's comfortable pre-revenue."],
              ["MARCUS·AI", "#8a8f98", "That's squarely Marcus's thesis — he leads seed rounds in dev infrastructure and has sat on two agent-tooling boards."],
              ["DANA·AI", ACCENT, "One filter: she only wants investors who can open doors to design partners, not just capital."],
              ["MARCUS·AI", "#8a8f98", "He has 40+ engineering leaders piloting agent tools right now. Worth a real conversation."],
            ].map(([who, color, line], i) => (
              <div key={i} style={{ display: "flex", gap: "10px" }}>
                <span style={{ flex: "none", fontFamily: MONO, fontSize: "10px", letterSpacing: "0.08em", color, width: "58px", paddingTop: "2px" }}>{who}</span>
                <p style={{ margin: 0, fontSize: "14.5px", lineHeight: 1.55, color: "#cfd2d8" }}>{line}</p>
              </div>
            ))}
          </div>
          {/* audio player */}
          <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 16px", background: "rgba(255,255,255,0.03)", borderRadius: "12px" }}>
            <button
              onClick={() => setAudioPlaying((p) => !p)}
              className="ga-btn ga-btn-primary"
              style={{ flex: "none", width: "38px", height: "38px", borderRadius: "50%", background: ACCENT, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {audioPlaying ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#0c0d10">
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#0c0d10">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <WaveBars playing={audioPlaying} />
            <span style={{ flex: "none", fontFamily: MONO, fontSize: "11px", color: "#7c818b" }}>1:48</span>
          </div>
          <div style={{ marginTop: "16px", fontSize: "13.5px", lineHeight: 1.5, color: "#9a9ea7" }}>
            <span style={{ color: ACCENT, fontWeight: 600 }}>Recommendation:</span> strong fit — worth a live intro in the Water Cooler.
          </div>
        </div>
      </section>
      </>
      )}

      {/* FOOTER */}
      <footer style={{ position: "relative", zIndex: 2, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
        {/* no logo lockup here — nothing scrolls, so it would sit on screen
            beside the nav's copy of itself */}
        <div style={{ maxWidth: "1140px", margin: "0 auto", padding: "30px 32px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "20px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "26px", fontSize: "13px", color: "#7c818b" }}>
            <a href="/hosts" onClick={goHosts} className="ga-foot">Run your own nexus</a>
            {appLinkOn && (
              <a href="https://mml-glonet.com" target="_blank" rel="noopener noreferrer" className="ga-foot">The app ↗</a>
            )}
            <span>© 2026</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
